import React, { useCallback, useState } from 'react';

import Button from '@atlaskit/button/new';
import type { SendAnalyticsEvent } from '@atlassian/conversation-assistant-instrumentation/types/analytics';

import type { DraftWorkItem } from '../../controllers/use-ai-create-project-context/types';
import { useCreateDraftWorkItems } from '../../controllers/useCreateDraftWorkItems';

export type OnClickSuccessProps = {
	invocationIdToCreatedIssueKey: Record<string, string>;
	cloudUrl: string;
};

export type OnClickErrorProps = {
	errorMessage: string;
};

export const CreateWorkItemsButton = ({
	getDraftWorkItems,
	buttonContent,
	testId,
	isDisabled = false,
	onClick,
	appearance = 'default',
	ariaLabel,
	...props
}: {
	getDraftWorkItems: () => Promise<DraftWorkItem[]>;
	onSuccess?: (args: OnClickSuccessProps) => void | Promise<void>;
	onError?: (args: OnClickErrorProps) => void;
	cloudId: string;
	cloudUrl: string;
	projectIdOrKey: string;
	buttonContent: React.ReactNode;
	testId?: string;
	isDisabled?: boolean;
	showErrorsInFlag?: boolean;
	showSuccessFlagOnCreate?: boolean;
	onClick?: () => void;
	appearance?: 'default' | 'primary';
	isStagingArea: boolean;
	entryPoint?: string;
	ariaLabel?: string;
	sendAifcAnalyticsEvent?: SendAnalyticsEvent;
}): React.JSX.Element => {
	const [isLoading, setIsLoading] = useState(false);

	const onCreateDraftWorkItems = useCreateDraftWorkItems(props);

	const handleOnClick = useCallback(async () => {
		onClick?.();
		setIsLoading(true);

		const draftWorkItems: DraftWorkItem[] = await getDraftWorkItems();
		await onCreateDraftWorkItems({
			draftWorkItems,
		});
		setIsLoading(false);
	}, [getDraftWorkItems, onCreateDraftWorkItems, onClick]);

	return (
		<Button
			onClick={handleOnClick}
			isLoading={isLoading}
			isDisabled={isDisabled}
			testId={testId}
			appearance={appearance}
			aria-label={ariaLabel}
		>
			{buttonContent}
		</Button>
	);
};
